import { useState, useEffect, useContext } from "react";
import { Link, NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  faPhone,
  faEnvelope,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FaPhoneAlt, FaEnvelope, FaFacebookF, FaTwitter, FaLinkedinIn, FaWhatsapp, FaYoutube, FaInstagram } from "react-icons/fa";
import {
  faWhatsapp,
  faFacebookF,
  faLinkedinIn,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import { ProductContext } from "../contexts/ProductContext";

export default function Navbar() {
  const [selectedCategory, setSelectedCategory] = useState(null); // { name, id }
  const [selectedProduct, setSelectedProduct] = useState(null); // { name, id }
  const [open, setOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleMenu = () => {
    setOpenMenu(!openMenu);
    setActiveIndex(null);
  };

  const toggleSubMenu = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setProductOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productError, setProductError] = useState("");

  const { products: ctxProducts, setProducts: setCtxProducts } = useContext(ProductContext);

  useEffect(() => {
    // if we already have raw products in context, build nav items from them
    if (ctxProducts && ctxProducts.length > 0) {
      buildNavItems(ctxProducts);
      setLoadingProducts(false);
      return;
    }

    async function fetchProducts() {
      setLoadingProducts(true);
      setProductError("");
      try {
        // BASE_URL import
        let BASE_URL;
        try {
          BASE_URL = (await import("../BASEURL")).default;
        } catch {
          BASE_URL = "";
        }
        const res = await fetch(`${BASE_URL}/api/product`);
        if (!res.ok) throw new Error("Failed to fetch products");
        const json = await res.json();
        const raw = json.data || [];
        setCtxProducts(raw);
        buildNavItems(raw);
      } catch (err) {
        setProductError(err.message || "Failed to fetch products");
        setLoadingProducts(false);
      }
    }
    fetchProducts();
  }, [ctxProducts]);

  const buildNavItems = (rawProducts) => {
    // Group products by category._id
    const grouped = {};
    (rawProducts || []).forEach((prod) => {
      const catObj = prod.category || { _id: "uncategorized", name: "Uncategorized" };
      const catId = catObj._id || "uncategorized";
      if (!grouped[catId]) grouped[catId] = { name: catObj.name || "Uncategorized", products: [] };
      grouped[catId].products.push(prod);
    });
    // Force category order as per user request
    const desiredOrder = [
      "Aeration Equipment",
      "Sludge Handling Systems",
      "Dosing Pumps & Agitators",
      "Filteration Equipment"
    ];

    // Per-category desired item ordering (items not listed will appear after these)
     const itemOrderMap = {
          "aeration equipment": [
            "Blowers",
            "Acoustic Hoods",
            "Tubular Diffusers",
            "Disc Diffusers",
            "MBBR Media",
            "Spare Parts / Accessories"
          ],
          "sludge handling systems": [
            "Sludge Dewatering",
            "Filter Press",
            "Tubesettler Media",
            "Screw Pump",
            "Centrifuge Machine"
          ],
          "dosing pumps & agitators": [
            "Electronic Dosing Pump",
            "Industrial Agitator / Mixer",
            "Hydraulic Pump",
            "Mechanical Pump",
            "Plunger Pump",
            "Dosing System"
          ],
          "filteration equipment": [
            "Vessels",
            "Pressure Tube",
            "Float Switch",
            "Pressure Guage",
            "Multiport Valve",
            "Distribution System",
            "Filter Sand",
            "Activated Carbon"
          ]
        };

    const arr = [];
    // Add desired categories in order if present (and sort items inside)
    desiredOrder.forEach((catName) => {
      const found = Object.keys(grouped).find(
        (catId) => grouped[catId].name === catName
      );
      if (found) {
        const prods = grouped[found].products || [];
        const key = (grouped[found].name || "").toLowerCase();
        const desiredItems = itemOrderMap[key];
        if (desiredItems && desiredItems.length > 0) {
          const idxMap = {};
          desiredItems.forEach((n, i) => (idxMap[n.toLowerCase()] = i));
          prods.sort((a, b) => {
            const nameA = ((a.name || "") + "").toLowerCase().trim();
            const nameB = ((b.name || "") + "").toLowerCase().trim();
            const ia = idxMap.hasOwnProperty(nameA) ? idxMap[nameA] : Infinity;
            const ib = idxMap.hasOwnProperty(nameB) ? idxMap[nameB] : Infinity;
            if (ia === ib) return 0;
            return ia - ib;
          });
        }
        arr.push({
          name: grouped[found].name,
          path: `/products`,
          children: prods.map((prod) => ({
            name: prod.name,
            path: `/product/${prod._id || prod.id}`,
          })),
        });
        delete grouped[found];
      }
    });
    // Add any remaining categories
    Object.keys(grouped).forEach((catId) => {
      arr.push({
        name: grouped[catId].name,
        path: `/products`,
        children: grouped[catId].products.map((prod) => ({
          name: prod.name,
          path: `/product/${prod._id || prod.id}`,
        })),
      });
    });
    setProducts(arr);
    setLoadingProducts(false);
  };

  const navLinkClass = ({ isActive }) =>
    isActive
      ? "text-primary font-semibold border-b-2 border-primary cursor-pointer"
      : "hover:text-primary transition cursor-pointer";

 
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      const multiplier = window.innerWidth >= 768 ? 0.38 : 0.27;
      const offset = window.innerHeight * multiplier; 
      window.scrollTo({ top: offset, behavior: "instant" });
    }
  };

  const handleCategoryClick = (item) => {
    setSelectedCategory({ name: item.name, id: item.path.split("/").pop() });
    scrollToTop();
  };

  const [contactDetails, setContactDetails] = useState({
    phone: "+91 75758 04585",
    email: "serawater007@gmail.com",
    address: "Shed No 53, Ambica Industrial Estate, Phase I, Vatva, Ahmedabad, Gujarat 382445.",
  });

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-dark/90 backdrop-blur border-b border-white/10">
        <div className="bg-[#1f2933] text-white text-sm hidden md:block">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col md:flex-row items-center justify-between gap-2">

            {/* LEFT INFO */}
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-1">
                <FaPhoneAlt className="text-primary" />
                <a href="tel:+9175758 04585" className="hover:underline hover:text-primary">
                  +91 75758 04585
                </a>
              </span>


              <span className="flex items-center gap-1">
                <FaEnvelope className="text-primary" />
                <a href="mailto:serawater007@gmail.com" className="hover:underline hover:text-primary">
                  serawater007@gmail.com
                </a>
              </span>
            </div>

            {/* SOCIAL ICONS */}
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/serawater"target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                <FaFacebookF />
              </a>
              <a href="https://www.youtube.com/@SeraWater007"target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                <FaYoutube />
              </a>
              <a href="https://www.instagram.com/sera.water/"target="_blank" rel="noopener noreferrer" className="hover:text-primary">
                <FaInstagram />
              </a>
              <a
                href="https://wa.link/+917575804585"
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary"
              >
                <FaWhatsapp />
              </a>
            </div>

          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">

          {/* LOGO */}
          <Link to="/" className="flex items-center" onClick={(e) => { e.preventDefault(); window.location.href = '/'; }}>
            <img
              src="/images/logo/sera-logo.png"
              className="h-12 md:h-16"
              alt="Sera Water Logo"
            />
          </Link>

          {/* DESKTOP MENU */}
          <div className="hidden md:flex gap-8 text-white items-center font-body text-lg">
            <NavLink to="/" end className={navLinkClass}>Home</NavLink>
            <NavLink to="/about" className={navLinkClass}>About</NavLink>

            <div className="relative group hidden md:block cursor-pointer">
              <NavLink to="/products#products-section" className={navLinkClass}>
                Products  
              </NavLink>

              {/* FIRST LEVEL DROPDOWN */}
              <div
                className="
      absolute left-0 top-full mt-2 w-64 bg-white shadow-lg rounded-md
      opacity-0 invisible group-hover:opacity-100 group-hover:visible
      transition-all duration-300 z-50
    "
              >
                <ul>
                  {products.map((item, index) => (
                    <li key={index} className="relative group/item">
                      {/* Parent Item */}
                      <NavLink
                        to={item.path + '#products-section'}
                        state={{ selectedCategory: { name: item.name, id: item.path.split('/').pop() } }}
                        className="flex justify-between items-center px-4 py-2 text-sm
             text-gray-700 hover:bg-primary/10 hover:text-primary"
                        onClick={() => handleCategoryClick(item)}
                      >
                        {item.name}
                        <span className="text-xs"></span>
                      </NavLink>

                      {/* SUB MENU */}
                      <div
                        className="
              absolute top-0 right-full ml-1 w-56 bg-white shadow-lg rounded-md
              opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible
              transition-all duration-300
            "
                        >
                        {item.children.map((child, idx) => (
                          <NavLink
                            key={idx}
                            to={child.path}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary/10 hover:text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              scrollToTop();
                              setSelectedProduct({ name: child.name, id: child.path.split('/').pop() });
                              window.location.href = child.path;
                            }}
                          >
                            {child.name}
                          </NavLink>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <NavLink to="/gallery" className={navLinkClass}>Gallery</NavLink>
            {/* <NavLink to="/blogs" className={navLinkClass}>Blogs</NavLink> */}
            <NavLink to="/contact#get-in-touch" className={navLinkClass}>Contact</NavLink>

          </div>

          {/* MOBILE PHONE ICON */}
          <a
            href="tel:+91 75758 04585"
            className="md:hidden flex items-center gap-3 text-white mr-4"
          >
            <FontAwesomeIcon icon={faPhone} className="text-2xl icon" />
          </a>
          {/* HAMBURGER */}
          <button
            className="md:hidden text-white text-2xl"
            onClick={() => setOpen(true)}
            aria-label="Open Menu"
          >
            <FontAwesomeIcon icon={faBars} className="icon" />
          </button>
        </div>

        {/* OVERLAY */}
        {open && (
          <div
            className="fixed inset-0 bg-black/80 z-40"
            onClick={() => setOpen(false)}
          />
        )}

        {/* MOBILE MENU */}
        <div
          className={`
            fixed top-0 right-0 h-screen overflow-y-auto w-full bg-dark z-50 pt-2 
            transform transition-transform duration-300
            ${open ? "translate-x-0" : "translate-x-full"}
          `}
        >
          {/* LOGO */}
          <Link to="/" className="flex items-center px-4" onClick={(e) => { e.preventDefault(); window.location.href = '/'; }}>
            <img
              src="/images/logo/sera-logo.png"
              className="h-16"
              alt="Sera Water Logo"
            />
          </Link>

          {/* CLOSE */}
          <div className="absolute top-4 right-4">
            <button
              className="text-white text-2xl"
              onClick={() => setOpen(false)}
              aria-label="Close Menu"
            >
              <FontAwesomeIcon icon={faXmark} className="icon" />
            </button>
          </div>


          {/* MOBILE LINKS */}
          <nav className="flex flex-col gap-6 px-8 pt-4 text-textGray font-body text-lg bg-dark h-screen">

            <NavLink to="/" end className={navLinkClass} onClick={() => setOpen(false)}>
              Home
            </NavLink>
            <NavLink to="/about" className={navLinkClass} onClick={() => setOpen(false)}>
              About
            </NavLink>


            <div className="w-full">

              {/* MAIN BUTTON */}
              <button
                onClick={toggleMenu}
                className="w-full flex justify-between items-center pr-4  text-lg font-semibold"
              >
                Products
                <span className={`transition ${openMenu ? "rotate-180" : ""}`}>▼</span>
              </button>

              {/* FIRST LEVEL */}
              <div
                className={`transition-all duration-300 overflow-hidden ${openMenu ? "max-h-[500px]" : "max-h-0"
                  }`}
              >
                {products.map((item, index) => (
                  <div key={index} className="">

                    {/* CATEGORY */}
                    <div className="w-full flex justify-between items-center px-6 py-3">
                      {/* LEFT SIDE → PAGE NAVIGATION */}
                      <Link
                        to={item.path}
                        state={{ selectedCategory: { name: item.name, id: item.path.split('/').pop() } }}
                        onClick={() => {
                          setOpen(false);
                          handleCategoryClick(item);
                        }}
                      >
                        {item.name}
                      </Link>

                      {/* RIGHT SIDE → DROPDOWN TOGGLE */}
                      <button
                        onClick={() => toggleSubMenu(index)}
                        className="text-sm transition-transform"
                      >
                        <span
                          className={`inline-block transition-transform ${
                            activeIndex === index ? "rotate-180" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </button>
                    </div>

                    {/* SUB ITEMS */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ${activeIndex === index ? "max-h-54" : "max-h-0"
                        }`}
                    >
                      <div className="pl-8 pb-2">
                        {item.children.map((child, i) => (
                          <NavLink
                            key={i}
                            to={child.path}
                            onClick={(e) => {
                              e.preventDefault();
                              setOpen(false);
                              scrollToTop();
                              setSelectedProduct({ name: child.name, id: child.path.split('/').pop() });
                              window.location.href = child.path;
                            }}
                            className="block py-2 text-sm text-gray-600 hover:text-primary"
                          >
                            {child.name}
                          </NavLink>
                        ))}
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            </div>

            <NavLink to="/gallery" className={navLinkClass} onClick={() => setOpen(false)}>
              Gallery
            </NavLink>
            {/* <NavLink to="/blogs" className={navLinkClass} onClick={() => setOpen(false)}>
              Blogs
            </NavLink> */}
            <NavLink to="/contact#get-in-touch" className={navLinkClass} onClick={() => setOpen(false)}>
              Contact
            </NavLink>

            {/* DIVIDER */}
            <div className="border-t border-white/20 "></div>

            {/* CONTACT DETAILS */}
            <div className="space-y-4 text-sm text-textGray">

              <a
                href="tel:+91 75758 04585" target="_blank" rel="noreferrer"
                className="flex items-center gap-3 hover:text-primary transition"
              >
                <FontAwesomeIcon icon={faPhone} className="text-xl" />
                <span>+91 75758 04585</span>
              </a>

              <a
                href="mailto:serawater007@gmail.com" target="_blank" rel="noreferrer"
                className="flex items-center gap-3 hover:text-primary transition pt-1"
              >
                <FontAwesomeIcon icon={faEnvelope} className="text-xl" />
                <span>serawater007@gmail.com</span>
              </a>

              <a
                href="https://maps.app.goo.gl/RaKXRTswPt5VWdQVA" target="_blank" rel="noreferrer"
                className="flex items-center gap-3 hover:text-primary transition"
              >
                <FontAwesomeIcon icon={faLocationDot} className="text-xl" />
                <span className="w-fit" >
                  Shed No 53, Ambica Industrial Estate, Phase I, Vatva, Ahmedabad, Gujarat 382445.
                </span>
              </a>


            </div>

            {/* SOCIAL ICONS */}
            <div className="flex gap-4 pb-20">
              <a
                href="https://wa.link/+917575804585"
                target="_blank"
                rel="noreferrer"
                className="icon-btn"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="icon" />
              </a>

              <a
                href="/"
                className="icon-btn" target="_blank" rel="noreferrer"
              >
                <FontAwesomeIcon icon={faLinkedinIn} className="icon" />
              </a>

              <a
                href="/"
                className="icon-btn" target="_blank" rel="noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} className="icon" />
              </a>

              <a
                href="/"
                className="icon-btn" target="_blank" rel="noreferrer"
              >
                <FontAwesomeIcon icon={faFacebookF} className="icon" />
              </a>
            </div>

          </nav>

        </div>
      </nav>

    </>
  );
}
